import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { Category, Offer, Product } from "@/types";

const OFFER_COLUMNS =
  "id, name, scope, product_id, category_id, discount_type, discount_value, special_price, start_date, end_date, active, created_at, updated_at";

export interface OffersData {
  offers: Offer[];
  products: Product[];
  categories: Category[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useOffersData(): OffersData {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [offersRes, productsRes, categoriesRes] = await Promise.all([
        supabase.from("offers").select(OFFER_COLUMNS).order("created_at", { ascending: false }),
        supabase
          .from("products")
          .select("id, name, sku, category_id, price, wholesale_price, active, image_url")
          .order("name"),
        supabase.from("product_categories").select("id, name, description, active").order("name"),
      ]);

      if (offersRes.error) {
        setError("No se pudieron cargar las ofertas.");
        setOffers([]);
        return;
      }

      const productList = (productsRes.data ?? []) as Product[];
      const categoryList = (categoriesRes.data ?? []) as Category[];
      const productMap = new Map(productList.map((p) => [p.id, p.name]));
      const categoryMap = new Map(categoryList.map((c) => [c.id, c.name]));

      const enriched = ((offersRes.data ?? []) as Offer[]).map((offer) => ({
        ...offer,
        product_name: offer.product_id ? productMap.get(offer.product_id) ?? null : null,
        category_name: offer.category_id ? categoryMap.get(offer.category_id) ?? null : null,
      }));

      setOffers(enriched);
      setProducts(productList);
      setCategories(categoryList);
    } catch {
      setError("No se pudieron cargar las ofertas.");
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { offers, products, categories, loading, error, reload: load };
}